import { Box, Button, Popover, Tab, Tabs, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { MouseEvent, useState } from 'react';
import { GetBusyGuardReason } from 'src/components/dispatch/resolveBusyGuardReason';
import { useUpdateVehicleColorMutation } from 'src/hooks/useUpdateVehicleColor.mutation';

import { DEFAULT_VEHICLE_COLORS } from '../vehicleColorPalette';

type ColorScope = 'truck' | 'partner_company';

type Props = {
  anchorEl: HTMLElement | null;
  truckId: number;
  currentTruckColor: string | null;
  partnerCompanyId: number | undefined;
  currentPartnerCompanyColor: string | null;
  /** 「対象」見出しに出す車両名(左リスト表示と同じ truckName || vehicleNumber) */
  truckName: string;
  /** 「対象」見出しに出す運送会社名。未設定なら null(タブは disabled) */
  partnerCompanyName: string | null;
  onClose: () => void;
  /**
   * 自動配車の実行中に競合する書き込みを弾く（Task 10 #7）。未指定ならガードしない。
   * onClick 実行時点の最新状態を読ませるため、値ではなく getter で受ける。
   */
  getBusyGuardReason?: GetBusyGuardReason;
};

/**
 * /map 左リストの色バークリックで開く車両カラー Popover。
 *
 * route-planner サンプル(VehicleColorPickerPopover.tsx)の Popover シェル/12色グリッド
 * レイアウトを流用しつつ、以下は本実装で読み替える(移植しないもの):
 * - localStorage 永続(useVehicleColorState) → 現在色は props(currentTruckColor /
 *   currentPartnerCompanyColor)で受け取る。真実は DB(server)。
 * - getVehicleColor の階層解決 / group スコープ → 廃止。スコープは 車両 / 運送会社 の 2 つのみ。
 * - 色選択/クリアは useUpdateVehicleColorMutation を呼び、反映は invalidate 任せ
 *   (mutation のレスポンス body は消費しない)。
 * - 失敗時通知は呼び出し側 mutate 第2引数の onError で行う(Presenter.tsx の配車確定・
 *   車両表示順 mutation と同じ peer パターン。hook 側の onSuccess はキャッシュ invalidate 専用)。
 */
export const VehicleColorPickerPopover = ({
  anchorEl,
  truckId,
  currentTruckColor,
  partnerCompanyId,
  currentPartnerCompanyColor,
  truckName,
  partnerCompanyName,
  onClose,
  getBusyGuardReason,
}: Props) => {
  const [scope, setScope] = useState<ColorScope>('truck');
  const { mutate } = useUpdateVehicleColorMutation();
  const { enqueueSnackbar } = useSnackbar();

  const partnerCompanyDisabled = partnerCompanyId == null;
  const activeScope = scope === 'partner_company' && partnerCompanyDisabled ? 'truck' : scope;
  const currentColor = activeScope === 'truck' ? currentTruckColor : currentPartnerCompanyColor;
  // 「対象」見出し: 選択中スコープの名前(車両名 / 運送会社名)を出す。
  const targetName = activeScope === 'truck' ? truckName : partnerCompanyName ?? '未設定';

  const handleMutationError = () => {
    enqueueSnackbar('車両色の更新に失敗しました', { variant: 'error' });
  };

  const applyColor = (color: string | null) => {
    // Task 10 #7: 色を選んだ時点(= mutate 直前)で busy を判定する。
    const busyReason = getBusyGuardReason?.();
    if (busyReason) {
      enqueueSnackbar(busyReason, { variant: 'error' });
      return;
    }
    if (activeScope === 'truck') {
      mutate({ scope: 'truck', color, truckId }, { onError: handleMutationError });
    } else {
      mutate({ scope: 'partner_company', color, partnerCompanyId }, { onError: handleMutationError });
    }
    onClose();
  };

  return (
    <Popover
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'center', horizontal: 'right' }}
      transformOrigin={{ vertical: 'center', horizontal: 'left' }}
      slotProps={{ paper: { sx: { p: 1.25, borderRadius: 2, minWidth: 220 } } }}
    >
      <Tabs
        value={activeScope}
        onChange={(_, value: ColorScope) => setScope(value)}
        variant="fullWidth"
        sx={{ minHeight: 32, mb: 1 }}
      >
        <Tab label="車両" value="truck" sx={{ minHeight: 32, py: 0.5, fontSize: 12 }} />
        <Tab
          label="運送会社"
          value="partner_company"
          disabled={partnerCompanyDisabled}
          sx={{ minHeight: 32, py: 0.5, fontSize: 12 }}
        />
      </Tabs>

      <Box sx={{ px: 0.5, pb: 0.75 }}>
        <Typography sx={{ fontSize: 10, color: 'text.secondary' }}>
          対象:{' '}
          <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
            {targetName}
          </Box>
        </Typography>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(6, 20px)', gap: 0.75, px: 0.5 }}>
        {DEFAULT_VEHICLE_COLORS.map((c) => {
          const selected = currentColor === c;
          return (
            <Box
              key={c}
              component="button"
              type="button"
              aria-label={`色 ${c}`}
              onClick={(e: MouseEvent<HTMLButtonElement>) => {
                e.stopPropagation();
                applyColor(c);
              }}
              sx={{
                width: 20,
                height: 20,
                borderRadius: '50%',
                background: c,
                cursor: 'pointer',
                p: 0,
                border: selected ? '2px solid' : '2px solid transparent',
                borderColor: selected ? 'text.primary' : 'transparent',
                boxShadow: selected ? '0 0 0 1px #fff inset' : 'none',
                transition: 'transform 0.1s',
                '&:hover': { transform: 'scale(1.15)' },
              }}
            />
          );
        })}
      </Box>

      {currentColor && (
        <Box sx={{ mt: 1, pt: 0.75, borderTop: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            size="small"
            onClick={() => applyColor(null)}
            sx={{ fontSize: 10, textTransform: 'none', color: 'text.secondary' }}
          >
            この{activeScope === 'truck' ? '車両' : '運送会社'}の色をクリア
          </Button>
        </Box>
      )}
    </Popover>
  );
};
